/**
 * Administración de usuarios. Listar, cambiar role/active, sincronizar
 * desde Google Workspace via Admin Directory API. Solo admin.
 */
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  RefreshCw,
  Mail,
  ShieldCheck,
  ShieldOff,
  MessageSquare,
  Layers,
  Banknote,
  Pencil,
  X,
  LogIn,
  Briefcase,
  Check,
} from "lucide-react";
import { USER_POSITION_SUGGESTIONS, type SessionUser } from "@axorial/shared";
import clsx from "clsx";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";

type Role = "admin" | "finanzas" | "lead" | "member";

interface UserRow {
  id: string;
  email: string;
  name: string;
  role: Role;
  position: string | null;
  active: boolean;
  mattermostUserId: string | null;
  planeUserId: string | null;
  rfc: string | null;
  curp: string | null;
  isPayrollWorker: boolean;
  workerType: "asimilado" | "becario" | "honorarios" | null;
  createdAt: number;
  updatedAt: number;
}

type WorkerType = "asimilado" | "becario" | "honorarios";

interface PayrollPatch {
  rfc?: string | null;
  curp?: string | null;
  isPayrollWorker?: boolean;
  workerType?: WorkerType | null;
}

interface SyncResult {
  ok: true;
  fetched: number;
  created: number;
  updated: number;
  unchanged: number;
  mattermostMatched: number;
  mattermostMissing: number;
  planeMatched: number;
  planeMissing: number;
  mattermostErrored: boolean;
  planeErrored: boolean;
}

const ROLE_LABEL: Record<Role, string> = {
  admin: "Admin",
  finanzas: "Finanzas",
  lead: "Lead",
  member: "Miembro",
};

const ROLE_BADGE: Record<Role, string> = {
  admin: "badge-error",
  finanzas: "badge-warning",
  lead: "badge-info",
  member: "badge-ghost",
};

export const Route = createFileRoute("/admin/users")({
  component: AdminUsersPage,
});

function AdminUsersPage() {
  const qc = useQueryClient();
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const { data, isLoading } = useQuery<{ users: UserRow[] }>({
    queryKey: ["admin", "users"],
    // Admin necesita ver inactivos para poder reactivarlos. `?all=1` el
    // backend lo respeta solo si el caller es admin.
    queryFn: () => api.get("/users?all=1"),
  });

  const { data: me } = useQuery<SessionUser>({
    queryKey: ["me"],
    queryFn: () => api.get("/me"),
  });

  // Impersonar: setea cookie firmada via POST y luego recarga la app para
  // que el resto del estado (sidebar, dashboard, etc.) refleje al nuevo
  // usuario. Recargar es más simple que tratar de invalidar todas las
  // queries en cadena.
  const impersonate = useMutation<{ ok: true }, Error, string>({
    mutationFn: (userId) => api.post(`/admin/impersonate/${userId}`),
    onSuccess: () => {
      window.location.href = "/";
    },
  });

  const sync = useMutation<SyncResult>({
    mutationFn: () => api.post("/users/sync-google"),
    onSuccess: (res) => {
      setSyncResult(res);
      setSyncError(null);
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (err) => {
      setSyncResult(null);
      setSyncError(err instanceof Error ? err.message : String(err));
    },
  });

  const updateUser = useMutation<
    { ok: true },
    Error,
    {
      id: string;
      patch: { role?: Role; active?: boolean; position?: string | null } & PayrollPatch;
    }
  >({
    mutationFn: ({ id, patch }) => api.patch(`/users/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });

  const [payrollDialog, setPayrollDialog] = useState<UserRow | null>(null);

  const users = data?.users ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="administración"
        title="Usuarios"
        subtitle="Gestiona el equipo. Roles globales se asignan aquí; los roles por proyecto se asignan desde la página del proyecto."
        actions={
          <div className="flex items-center gap-2">
            <Link to="/admin" className="btn btn-ghost btn-sm">
              <ArrowLeft className="size-4" /> Admin
            </Link>
            <button
              type="button"
              onClick={() => sync.mutate()}
              disabled={sync.isPending}
              className="btn btn-primary btn-sm"
            >
              <RefreshCw className={clsx("size-3.5", sync.isPending && "animate-spin")} />
              Sincronizar Google Workspace
            </button>
          </div>
        }
      />

      {syncResult && (
        <div className="alert alert-success items-start">
          <ShieldCheck className="size-4 mt-0.5" />
          <div className="text-sm flex-1">
            <div>
              Google Workspace: <strong>{syncResult.created} nuevos</strong> · {syncResult.updated}{" "}
              actualizados · {syncResult.unchanged} sin cambios (de {syncResult.fetched} en el
              dominio).
            </div>
            <div className="mt-1 text-xs opacity-90">
              Vinculación por email:{" "}
              {syncResult.mattermostErrored ? (
                <span className="text-warning">Mattermost falló (ver consola).</span>
              ) : (
                <>
                  <strong>{syncResult.mattermostMatched}</strong> en Mattermost
                  {syncResult.mattermostMissing > 0 && (
                    <> · {syncResult.mattermostMissing} sin cuenta</>
                  )}
                </>
              )}
              {" · "}
              {syncResult.planeErrored ? (
                <span className="text-warning">Plane falló (ver consola).</span>
              ) : (
                <>
                  <strong>{syncResult.planeMatched}</strong> en Plane
                  {syncResult.planeMissing > 0 && <> · {syncResult.planeMissing} sin cuenta</>}
                </>
              )}
              .
            </div>
          </div>
          <button className="btn btn-ghost btn-xs" onClick={() => setSyncResult(null)}>
            ✕
          </button>
        </div>
      )}

      {syncError && (
        <div className="alert alert-error">
          <ShieldOff className="size-4" />
          <div className="text-sm">
            <div className="font-medium">No pude sincronizar.</div>
            <div className="text-xs opacity-90 mt-1">{syncError}</div>
            <div className="text-xs opacity-75 mt-1">
              Si es la primera vez, configura <code className="font-mono">GOOGLE_SA_*</code> en los
              secrets del Worker — ve{" "}
              <code className="font-mono">docs/google-workspace-setup.md</code>.
            </div>
          </div>
          <button className="btn btn-ghost btn-xs" onClick={() => setSyncError(null)}>
            ✕
          </button>
        </div>
      )}

      <div className="card-elevated">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
            Equipo
          </h3>
          <span className="badge badge-ghost badge-sm tabular-nums">{users.length}</span>
        </div>

        {isLoading ? (
          <div className="grid place-items-center py-12">
            <span className="loading loading-spinner text-primary" />
          </div>
        ) : users.length === 0 ? (
          <div className="px-5 pb-8 text-sm text-base-content/60 text-center">
            Aún no hay usuarios. Click en <strong>Sincronizar Google Workspace</strong> para
            importarlos.
          </div>
        ) : (
          <ul className="divide-y divide-base-300/50">
            {users.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                onChangeRole={(role) => updateUser.mutate({ id: u.id, patch: { role } })}
                onChangePosition={(position) =>
                  updateUser.mutate({ id: u.id, patch: { position } })
                }
                onToggleActive={() => updateUser.mutate({ id: u.id, patch: { active: !u.active } })}
                onTogglePayroll={() =>
                  updateUser.mutate({
                    id: u.id,
                    patch: { isPayrollWorker: !u.isPayrollWorker },
                  })
                }
                onEditPayroll={() => setPayrollDialog(u)}
                onImpersonate={
                  me && me.id !== u.id && u.active
                    ? () => {
                        if (
                          window.confirm(
                            `Verás el panel como ${u.name}. La sesión queda en solo lectura y expira en 1 hora.`,
                          )
                        ) {
                          impersonate.mutate(u.id);
                        }
                      }
                    : undefined
                }
                pending={updateUser.isPending && updateUser.variables?.id === u.id}
              />
            ))}
          </ul>
        )}
      </div>
      {payrollDialog && (
        <PayrollDialog
          user={payrollDialog}
          onClose={() => setPayrollDialog(null)}
          onSave={(patch) =>
            updateUser.mutate(
              { id: payrollDialog.id, patch },
              { onSuccess: () => setPayrollDialog(null) },
            )
          }
          pending={updateUser.isPending}
        />
      )}
    </div>
  );
}

function UserRow({
  user,
  onChangeRole,
  onChangePosition,
  onToggleActive,
  onTogglePayroll,
  onEditPayroll,
  onImpersonate,
  pending,
}: {
  user: UserRow;
  onChangeRole: (role: Role) => void;
  onChangePosition: (position: string | null) => void;
  onToggleActive: () => void;
  onTogglePayroll: () => void;
  onEditPayroll: () => void;
  onImpersonate?: () => void;
  pending: boolean;
}) {
  return (
    <li
      className={clsx(
        "px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3",
        !user.active && "opacity-55",
      )}
    >
      <div className="size-9 rounded-full bg-primary/15 text-primary grid place-items-center text-sm font-semibold shrink-0">
        {initials(user.name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate flex items-center gap-2 flex-wrap">
          {user.name}
          {!user.active && <span className="badge badge-xs badge-ghost">Inactivo</span>}
          <PositionEditor value={user.position} disabled={pending} onSave={onChangePosition} />
        </div>
        <div className="text-xs text-base-content/55 mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <Mail className="size-3" /> {user.email}
          </span>
          <LinkBadge
            connected={!!user.mattermostUserId}
            label="MM"
            title={user.mattermostUserId ? "Vinculado a Mattermost" : "Sin cuenta de Mattermost"}
            icon={<MessageSquare className="size-2.5" />}
          />
          <LinkBadge
            connected={!!user.planeUserId}
            label="Plane"
            title={user.planeUserId ? "Vinculado a Plane" : "Sin cuenta de Plane"}
            icon={<Layers className="size-2.5" />}
          />
          <LinkBadge
            connected={user.isPayrollWorker}
            label="Nómina"
            title={
              user.isPayrollWorker
                ? `Asimilado · ${user.rfc ?? "sin RFC"}`
                : "No marcado como trabajador de nómina"
            }
            icon={<Banknote className="size-2.5" />}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={onTogglePayroll}
          disabled={pending}
          className={clsx(
            "btn btn-xs",
            user.isPayrollWorker ? "btn-success btn-outline" : "btn-ghost",
          )}
          title={
            user.isPayrollWorker
              ? "Quitar como trabajador de nómina"
              : "Marcar como trabajador de nómina (aparece en el selector de asimilados)"
          }
        >
          <Banknote className="size-3" />
          {user.isPayrollWorker ? "Nómina" : "Sin nómina"}
        </button>
        {user.isPayrollWorker && (
          <button
            type="button"
            onClick={onEditPayroll}
            disabled={pending}
            className="btn btn-ghost btn-xs"
            title="Editar RFC, CURP, bruto default"
          >
            <Pencil className="size-3" />
          </button>
        )}
        <span className={clsx("badge badge-xs hidden md:inline-flex", ROLE_BADGE[user.role])}>
          {ROLE_LABEL[user.role]}
        </span>
        <select
          value={user.role}
          onChange={(e) => onChangeRole(e.target.value as Role)}
          disabled={pending}
          className="select select-bordered select-xs w-32"
          aria-label={`Rol de ${user.name}`}
        >
          <option value="member">Miembro</option>
          <option value="lead">Lead</option>
          <option value="finanzas">Finanzas</option>
          <option value="admin">Admin</option>
        </select>
        {onImpersonate && (
          <button
            type="button"
            onClick={onImpersonate}
            disabled={pending}
            className="btn btn-ghost btn-xs"
            title={`Ver el panel como ${user.name} (solo lectura, 1h)`}
            aria-label={`Login as ${user.name}`}
          >
            <LogIn className="size-3" />
          </button>
        )}
        <button
          type="button"
          onClick={onToggleActive}
          disabled={pending}
          className={clsx("btn btn-xs", user.active ? "btn-ghost" : "btn-warning")}
          title={user.active ? "Desactivar" : "Activar"}
        >
          {user.active ? "Desactivar" : "Activar"}
        </button>
      </div>
    </li>
  );
}

function LinkBadge({
  connected,
  label,
  title,
  icon,
}: {
  connected: boolean;
  label: string;
  title: string;
  icon: React.ReactNode;
}) {
  return (
    <span
      title={title}
      className={clsx(
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium",
        connected
          ? "bg-success/15 text-success"
          : "bg-base-200/60 text-base-content/35 line-through decoration-1",
      )}
    >
      {icon}
      {label}
    </span>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * Edita datos de nómina del user: RFC, CURP y bruto mensual default.
 * Estos campos pre-rellenan el form de tx asimilada cuando el operador
 * selecciona al trabajador del catálogo.
 */
function PayrollDialog({
  user,
  onClose,
  onSave,
  pending,
}: {
  user: UserRow;
  onClose: () => void;
  onSave: (patch: PayrollPatch) => void;
  pending: boolean;
}) {
  const [rfc, setRfc] = useState(user.rfc ?? "");
  const [curp, setCurp] = useState(user.curp ?? "");
  const [workerType, setWorkerType] = useState<WorkerType | "">(user.workerType ?? "");

  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box max-w-md">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-base-content/55">Nómina</div>
            <h3 className="font-display font-semibold text-lg">{user.name}</h3>
          </div>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm btn-square">
            <X className="size-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-base-content/60">
              RFC
            </span>
            <input
              className="input input-bordered w-full uppercase font-mono"
              placeholder="XAXX010101000"
              maxLength={13}
              value={rfc}
              onChange={(e) => setRfc(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-base-content/60">
              CURP
            </span>
            <input
              className="input input-bordered w-full uppercase font-mono"
              placeholder="XAXX010101HDFXXX01"
              maxLength={18}
              value={curp}
              onChange={(e) => setCurp(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-base-content/60">
              Tipo de trabajador
            </span>
            <select
              className="select select-bordered w-full"
              value={workerType}
              onChange={(e) => setWorkerType((e.target.value || "") as WorkerType | "")}
            >
              <option value="">Asimilado (default · Art. 96 LISR)</option>
              <option value="asimilado">Asimilado (explícito)</option>
              <option value="becario">Becario (IVA aplica si hay factura)</option>
              <option value="honorarios">Honorarios (Art. 100 LISR)</option>
            </select>
            <span className="text-[11px] text-base-content/55 leading-relaxed">
              Becario permite capturar IVA en sus transacciones; los demás lo
              fuerzan a 0.
            </span>
          </div>
        </div>

        <div className="modal-action">
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
            Cancelar
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={() =>
              onSave({
                rfc: rfc.trim() || null,
                curp: curp.trim() || null,
                workerType: workerType === "" ? null : workerType,
              })
            }
            className="btn btn-primary btn-sm"
          >
            {pending && <span className="loading loading-spinner loading-xs" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Editor inline de "puesto". Chip clickeable; al click expande un
 * combobox custom con el input + dropdown anclado abajo. El dropdown
 * filtra `USER_POSITION_SUGGESTIONS` según lo que va tecleando el user;
 * click en una sugerencia la commitea. Enter guarda lo escrito (puede
 * ser un valor custom que no está en la lista). Esc cancela.
 *
 * Por qué custom en vez de `<datalist>`: el datalist nativo el browser
 * lo posiciona arriba o abajo según le da la gana — el cliente pidió
 * que siempre salga abajo.
 */
function PositionEditor({
  value,
  disabled,
  onSave,
}: {
  value: string | null;
  disabled: boolean;
  onSave: (position: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const inputRef = useRef<HTMLInputElement>(null);
  // Posición del dropdown en viewport. Lo dibujamos vía portal con
  // position:fixed para que viva sobre el resto del DOM (los `<li>`
  // siguientes tapaban un absolute child del row).
  const [dropPos, setDropPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    if (!editing) {
      setDropPos(null);
      return;
    }
    const update = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (!r) return;
      setDropPos({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [editing]);

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(value ?? "");
          setEditing(true);
        }}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 text-xs text-base-content/70 hover:text-primary group px-2 py-0.5 rounded-md border border-base-300/50 hover:border-primary/40"
        title="Editar puesto"
      >
        <Briefcase className="size-3 text-base-content/45 group-hover:text-primary" />
        {value ? (
          <span className="font-medium">{value}</span>
        ) : (
          <span className="italic text-base-content/40">Sin puesto</span>
        )}
        <Pencil className="size-2.5 opacity-0 group-hover:opacity-60 transition-opacity" />
      </button>
    );
  }

  const commit = (override?: string) => {
    const raw = override ?? draft;
    const trimmed = raw.trim();
    const next = trimmed.length === 0 ? null : trimmed;
    if (next !== value) onSave(next);
    setEditing(false);
  };

  // Filtrado case-insensitive. Vacío = todas las sugerencias.
  const needle = draft.trim().toLowerCase();
  const suggestions = needle
    ? USER_POSITION_SUGGESTIONS.filter((s) => s.toLowerCase().includes(needle))
    : USER_POSITION_SUGGESTIONS;

  return (
    <div className="inline-flex items-center gap-1.5">
      <Briefcase className="size-3 text-base-content/45" />
      <input
        ref={inputRef}
        type="text"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
          if (e.key === "Escape") setEditing(false);
        }}
        onBlur={() => setTimeout(() => setEditing(false), 150)}
        placeholder="Puesto"
        className="input input-bordered input-xs w-full max-w-xs sm:w-56"
        maxLength={80}
      />
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => commit()}
        className="btn btn-ghost btn-xs"
        aria-label="Guardar"
      >
        <Check className="size-3" />
      </button>
      {dropPos &&
        suggestions.length > 0 &&
        createPortal(
          <ul
            className="fixed z-50 max-h-64 overflow-y-auto rounded-md border border-base-300 bg-base-100 shadow-lg py-1"
            style={{
              top: dropPos.top,
              left: dropPos.left,
              width: dropPos.width,
            }}
            // Evita que el mousedown haga blur al input antes del click.
            onMouseDown={(e) => e.preventDefault()}
          >
            {suggestions.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  onClick={() => commit(s)}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-base-200 transition-colors"
                >
                  {s}
                </button>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}
